import { useState, useEffect } from 'react';
import { Bell, X } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { supabase } from '@/integrations/supabase/client';

interface BannerHeading {
  id: string;
  title: string;
  content: string;
}

interface BannerData {
  id: string;
  title: string;
  content: string;
  banner_text: string;
  pdf_url?: string;
  pdf_filename?: string;
  headings?: BannerHeading[];
  is_enabled: boolean;
}

const NotificationBanner = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [bannerData, setBannerData] = useState<BannerData | null>(null);
  const [loading, setLoading] = useState(true);
  const [showDismissDialog, setShowDismissDialog] = useState(false);

  useEffect(() => {
    fetchBannerData();
  }, []);

  const fetchBannerData = async () => {
    try {
      const { data, error } = await supabase
        .from('notification_banners')
        .select('*')
        .eq('is_enabled', true)
        .limit(1)
        .maybeSingle();

      if (error) {
        console.warn('Banner fetch failed silently:', error.message);
        setIsVisible(false);
        return;
      }

      if (data) {
        // Check if this banner was dismissed by the user
        const dismissedBanners = JSON.parse(localStorage.getItem('dismissedBanners') || '[]');
        const wasDismissed = dismissedBanners.includes(data.id);
        
        setBannerData({
          ...data,
          headings: Array.isArray(data.headings) ? data.headings as unknown as BannerHeading[] : []
        });
        setIsVisible(!wasDismissed);
      } else {
        setIsVisible(false);
      }
    } catch (error) {
      // Silently handle errors to prevent console noise
      setIsVisible(false);
    } finally {
      setLoading(false);
    }
  };

  const handleDismiss = () => {
    setShowDismissDialog(true);
  };

  const handleConfirmDismiss = () => {
    if (bannerData) {
      const dismissedBanners = JSON.parse(localStorage.getItem('dismissedBanners') || '[]');
      dismissedBanners.push(bannerData.id);
      localStorage.setItem('dismissedBanners', JSON.stringify(dismissedBanners));
    }
    setIsVisible(false);
    setShowDismissDialog(false);
  };

  const handleCancelDismiss = () => {
    setShowDismissDialog(false);
  };

  if (loading || !isVisible || !bannerData) return null;

  return (
    <>
      <div className={cn(
        "fixed top-20 w-full bg-primary/10 border-b border-primary/20 px-4 py-2 z-[30] md:z-[40]",
        "animate-[pulse_40s_ease-in-out_infinite] hover:animate-none transition-all duration-3000"
      )}>
        <div className="max-w-7xl mx-auto flex items-center justify-between gap-3 md:justify-center md:relative">
          <div className="flex items-center gap-3 flex-1 md:flex-none min-w-0">
            <Bell className="h-4 w-4 text-primary animate-bounce flex-shrink-0" />
            <span className="text-sm font-medium text-foreground truncate">
              {bannerData.banner_text}
            </span>
          </div>
          <div className="flex items-center gap-1 flex-shrink-0 md:absolute md:right-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsOpen(true)}
              className="text-primary hover:bg-primary/20 text-xs px-2 py-1 h-8 min-w-[50px]"
            >
              Details
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleDismiss}
              className="p-1 h-8 w-8 hover:bg-destructive/20 flex items-center justify-center"
            >
              <X className="h-3 w-3" />
            </Button>
          </div>
        </div>
      </div>

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bell className="h-5 w-5 text-primary" />
              {bannerData.title}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 text-sm">
            {/* Custom Content */}
            <div className="whitespace-pre-wrap text-muted-foreground">
              {bannerData.content.replace(/\\n/g, '\n').split('\n').map((line, index) => (
                <div key={index}>
                  {line.split(/(\*\*.*?\*\*)/).map((part, partIndex) => 
                    part.startsWith('**') && part.endsWith('**') ? (
                      <strong key={partIndex} className="font-semibold text-foreground">
                        {part.slice(2, -2)}
                      </strong>
                    ) : (
                      part
                    )
                  )}
                </div>
              ))}
            </div>

            {/* PDF Display */}
            {bannerData.pdf_url && (
              <div className="border border-border rounded-lg overflow-hidden">
                <div className="bg-muted p-3 flex items-center gap-2">
                  <Bell className="h-4 w-4 text-primary" />
                  <span className="font-medium">{bannerData.pdf_filename || 'Dokument'}</span>
                </div>
                <iframe
                  src={bannerData.pdf_url}
                  className="w-full h-96"
                  title="Banner PDF"
                />
              </div>
            )}

            {/* Dynamic Headings */}
            {bannerData.headings && bannerData.headings.map((heading) => (
              <div key={heading.id}>
                <h3 className="font-semibold text-base mb-3 text-primary">
                  {heading.title}
                </h3>
                <div className="space-y-2 text-muted-foreground whitespace-pre-wrap">
                  {heading.content.split('\n').map((line, index) => (
                    <div key={index}>
                      {line.split(/(\*\*.*?\*\*)/).map((part, partIndex) => 
                        part.startsWith('**') && part.endsWith('**') ? (
                          <strong key={partIndex} className="font-semibold text-foreground">
                            {part.slice(2, -2)}
                          </strong>
                        ) : (
                          part
                        )
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}

            <div className="border-t pt-4">
              <p className="text-muted-foreground">
                Liebe Grüße und bis bald!<br />
                <strong>Prof. Alois Tanzmeister</strong><br />
                (Obmann ASC-Fechten Wien)
              </p>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dismiss Confirmation Dialog */}
      <Dialog open={showDismissDialog} onOpenChange={setShowDismissDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Banner ausblenden</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Möchten Sie dieses Banner dauerhaft ausblenden? Es wird erst wieder angezeigt, wenn der Inhalt geändert wird.
            </p>
            <div className="flex gap-2 justify-end">
              <Button
                variant="outline"
                onClick={handleCancelDismiss}
                size="sm"
              >
                Abbrechen
              </Button>
              <Button
                onClick={handleConfirmDismiss}
                size="sm"
              >
                Dauerhaft ausblenden
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default NotificationBanner;